import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    InputLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import React, { useEffect, useState } from "react";
import { MILLIS_TO_LOCALE_DIGIT_FORMAT } from "../../../utilities/helpers/formatUnix";
import SearchIcon from "@mui/icons-material/Search";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { METADATA_MANAGEMENT } from "../LandingPage/constants";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Field, Form, Formik, useFormik } from "formik";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import moment from "moment-timezone";
import { enqueueSnackbar } from "notistack";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../utils/permissions";

dayjs.extend(utc);
dayjs.extend(timezone);

const MetadataForm = ({ formValue, refreshList }: any) => {
    const { hasPermission } = usePermissions();
    const timezoneCode = moment.tz(moment.tz.guess()).zoneAbbr();

    const initialState = {
        playbackStartDate: null,
        playbackEndDate: null,
        comingSoonDate: null,
        newSeasonDate: null,
    };
    const [enableEdit, setEnableEdit] = useState(true); // edit button show/hide
    const [formState, setFormState] = useState<any>(initialState);
    const [loader, setloader] = useState(false);
    const [assetId, setAssetId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingValues, setPendingValues] = useState<any>(null);

    useEffect(() => {
        if (formValue) {
            setFormState(formValue);
            formik.setValues(serializePayload(formValue)); // format the payload to support form
            setEnableEdit(true);
            setAssetId(formValue.id);
        }
    }, [formValue]);

    const serializePayload = (formValue: any) => {
        return {
            playbackStartDate: formValue?.playback_start_date || null,
            playbackEndDate: formValue?.playback_end_date || null,
            comingSoonDate: formValue?.coming_soon_date || null,
            newSeasonDate: formValue?.new_season_date || null,
        };
    };

    const deserializePayload = (formValue: any) => {
        return {
            ...formValue,
            playback_start_date: formValue.playbackStartDate,
            playback_end_date: formValue.playbackEndDate,
            coming_soon_date: formValue.comingSoonDate,
            new_season_date: formValue.newSeasonDate,
        };
    };

    const formik = useFormik({
        initialValues: initialState,
        enableReinitialize: true,
        onSubmit: (values: any) => {
            console.log("Form submitted with values:", values);
            // Check if content type requires confirmation (parent types that may have trailers/extras)
            const contentType = formState?.content_type?.toLowerCase();
            if (
                contentType === "series" ||
                contentType === "season" ||
                contentType === "movie"
            ) {
                setPendingValues(values);
                setConfirmDialogOpen(true);
            } else {
                saveMetadata(values);
            }
        },
    });

    const handleEditClick = () => {
        setEnableEdit(false);
    };

    const handleCancelClick = () => {
        setEnableEdit(true);
        formik.setValues(serializePayload(formState));
    };

    const handleSubmit = () => {
        formik.handleSubmit();
    };

    const handleConfirmDialogClose = () => {
        setConfirmDialogOpen(false);
        setPendingValues(null);
    };

    const handleConfirmApply = (applyToChildren: boolean) => {
        if (pendingValues) {
            saveMetadata(pendingValues, applyToChildren);
        }
        setConfirmDialogOpen(false);
        setPendingValues(null);
    };

    const getDialogContent = () => {
        const contentType = formState?.content_type?.toLowerCase();

        if (contentType === "series") {
            return {
                title: "Updating Avails to a Series",
                message:
                    "Would you like to apply these changes to all associated Seasons, Episodes, trailers & extras?",
                applyButtonText: "Apply to Series Only",
                applyAllButtonText: "Apply to All",
            };
        } else if (contentType === "season") {
            return {
                title: "Updating Avails to a Season",
                message:
                    "Would you like to apply these changes to all associated Episodes, trailers & extras?",
                applyButtonText: "Apply to Season Only",
                applyAllButtonText: "Apply to All",
            };
        } else if (contentType === "movie") {
            return {
                title: "Updating Avails to a Movie",
                message:
                    "Would you like to apply these changes to all associated trailers & extras?",
                applyButtonText: "Apply to Movie Only",
                applyAllButtonText: "Apply to All",
            };
        }
        return {
            title: "",
            message: "",
            applyButtonText: "",
            applyAllButtonText: "",
        };
    };

    const saveMetadata = async (
        values: any,
        applyToChildren: boolean = false
    ) => {
        try {
            setloader(true);
            const currentState = formState?.state?.toLowerCase();
            const isUpdatingDates =
                values.playbackStartDate != null ||
                values.playbackEndDate != null ||
                values.comingSoonDate != null ||
                values.newSeasonDate != null;
            // Any update of dates for a non-active asset transitions it back to active
            const payload: any = {
                ...values,
                applyToChildren, // Include flag to inform backend (applies to trailers & extras when present)
            };
            if (
                currentState &&
                currentState !== "active" &&
                isUpdatingDates
            ) {
                payload.state = "active";
            }

            const updateMetadata = await axios.put(
                `${process.env.BASE_URL}api/v1/cms/metadata/update/${assetId}`,
                payload
            );

            if (updateMetadata.data?.success) {
                refreshList(); // refresh the parent list with updated data
                setFormState({
                    ...formState,
                    ...deserializePayload(values),
                }); // reset the form. so that old data does not show up on form
                enqueueSnackbar("Metadata updated successfully!", {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(updateMetadata.data?.message, {
                    variant: "error",
                });
            }
            setEnableEdit(false);
        } catch (error: any) {
            console.log(error?.response?.data?.message);
            console.log("Error in getting master information", error);
        } finally {
            setloader(false);
        }
    };

    const handleDateChange = (fieldName: string, value: any) => {
        if (dayjs(value).isValid()) {
            formik.setFieldValue(fieldName, value.toISOString() || null);
        }
    };

    const dialogContent = getDialogContent();

    return (
        <div>
            <div className="ingest-header-refresh">
                <h3>Edit Metadata</h3>
            </div>
            <Grid spacing={2} container>
                <Grid item md={4} sm={4} lg={4} xl={4}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid md={2} sm={2} lg={2} xl={2} item>
                            <Typography variant="body1"> Name</Typography>
                        </Grid>
                        <Grid md={8} sm={8} lg={8} xl={8} item>
                            <TextField
                                multiline
                                fullWidth
                                maxRows={4}
                                value={formState?.name}
                                disabled={true}
                                variant="standard"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item md={4} sm={4} lg={4} xl={4}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid md={4} sm={4} lg={4} xl={4} item>
                            <Typography variant="body1">
                                {" "}
                                Reference ID
                            </Typography>
                        </Grid>
                        <Grid md={8} sm={8} lg={8} xl={8} item>
                            <TextField
                                multiline
                                fullWidth
                                maxRows={4}
                                value={formState?.reference_id}
                                disabled={true}
                                variant="standard"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item md={4} sm={4} lg={4} xl={4}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid md={3} sm={3} lg={3} xl={3} item>
                            <Typography variant="body1">
                                {" "}
                                Content Type
                            </Typography>
                        </Grid>
                        <Grid md={8} sm={8} lg={8} xl={8} item>
                            <TextField
                                multiline
                                fullWidth
                                maxRows={4}
                                value={formState?.content_type}
                                disabled={true}
                                variant="standard"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid>
                <form>
                    <TableContainer
                        className="mt-2"
                        component={Paper}
                        style={{ border: "1px solid #e0e0e0", width: "60%" }}
                    >
                        <Table>
                            <TableHead>
                                {hasPermission(
                                    PERMISSIONS.metadata,
                                    ACTION.EDIT
                                ) && (
                                    <TableRow>
                                        <TableCell>
                                            <div className="d-flex j-space">
                                                <Typography variant="body1">
                                                    Date
                                                </Typography>

                                                {enableEdit ? (
                                                    <Button
                                                        variant="contained"
                                                        startIcon={<EditIcon />}
                                                        onClick={() =>
                                                            handleEditClick()
                                                        }
                                                        disabled={!formValue}
                                                    >
                                                        Edit
                                                    </Button>
                                                ) : (
                                                    <div>
                                                        <Button
                                                            variant="contained"
                                                            color="warning"
                                                            startIcon={
                                                                <CancelIcon />
                                                            }
                                                            onClick={() =>
                                                                handleCancelClick()
                                                            }
                                                            disabled={loader}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            className="ml-1"
                                                            variant="contained"
                                                            startIcon={
                                                                <>
                                                                    {loader ? (
                                                                        <CircularProgress
                                                                            className="white"
                                                                            size={
                                                                                20
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <SaveIcon />
                                                                    )}
                                                                </>
                                                            }
                                                            onClick={() =>
                                                                handleSubmit()
                                                            }
                                                            disabled={loader}
                                                        >
                                                            Save
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>

                                        {/* Add more header cells as needed */}
                                    </TableRow>
                                )}
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <Grid style={{ padding: 10 }} container>
                                        <Grid
                                            item
                                            lg={6}
                                            md={12}
                                            xl={5}
                                            sm={12}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                            >
                                                <InputLabel>
                                                    Playback Start Date
                                                </InputLabel>
                                                <DateTimePicker
                                                    closeOnSelect={false}
                                                    value={
                                                        formik.values
                                                            .playbackStartDate &&
                                                        dayjs(
                                                            formik.values
                                                                .playbackStartDate
                                                        )
                                                    }
                                                    onChange={(value: any) =>
                                                        handleDateChange(
                                                            "playbackStartDate",
                                                            value
                                                        )
                                                    }
                                                    disabled={enableEdit}
                                                    className="datetime-picker"
                                                    format={`MM/DD/YYYY hh:mm:ss A ${timezoneCode}`}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid
                                            item
                                            lg={6}
                                            md={12}
                                            xl={5}
                                            sm={12}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                            >
                                                <InputLabel>
                                                    Playback End Date
                                                </InputLabel>
                                                <DateTimePicker
                                                    closeOnSelect={false}
                                                    value={
                                                        formik.values
                                                            .playbackEndDate &&
                                                        dayjs(
                                                            formik.values
                                                                .playbackEndDate
                                                        )
                                                    }
                                                    onChange={(value: any) =>
                                                        handleDateChange(
                                                            "playbackEndDate",
                                                            value
                                                        )
                                                    }
                                                    disabled={enableEdit}
                                                    className="datetime-picker"
                                                    format={`MM/DD/YYYY hh:mm:ss A ${timezoneCode}`}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    </Grid>
                                </TableRow>
                                <TableRow>
                                    <Grid style={{ padding: 10 }} container>
                                        <Grid
                                            item
                                            lg={6}
                                            md={12}
                                            xl={5}
                                            sm={12}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                            >
                                                <InputLabel>
                                                    Coming Soon Date
                                                </InputLabel>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <DateTimePicker
                                                        closeOnSelect={false}
                                                        value={
                                                            formik.values
                                                                .comingSoonDate &&
                                                            dayjs(
                                                                formik.values
                                                                    .comingSoonDate
                                                            )
                                                        }
                                                        onChange={(
                                                            value: any
                                                        ) =>
                                                            handleDateChange(
                                                                "comingSoonDate",
                                                                value
                                                            )
                                                        }
                                                        disabled={enableEdit}
                                                        className="datetime-picker"
                                                        format={`MM/DD/YYYY hh:mm:ss A ${timezoneCode}`}
                                                    />
                                                    {!enableEdit && (
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                formik.setFieldValue(
                                                                    "comingSoonDate",
                                                                    null
                                                                )
                                                            }
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Box>
                                            </LocalizationProvider>
                                        </Grid>
                                        <Grid
                                            item
                                            lg={6}
                                            md={12}
                                            xl={5}
                                            sm={12}
                                        >
                                            <LocalizationProvider
                                                dateAdapter={AdapterDayjs}
                                            >
                                                <InputLabel>
                                                    New Season Date
                                                </InputLabel>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <DateTimePicker
                                                        closeOnSelect={false}
                                                        value={
                                                            formik.values
                                                                .newSeasonDate &&
                                                            dayjs(
                                                                formik.values
                                                                    .newSeasonDate
                                                            )
                                                        }
                                                        onChange={(
                                                            value: any
                                                        ) =>
                                                            handleDateChange(
                                                                "newSeasonDate",
                                                                value
                                                            )
                                                        }
                                                        disabled={enableEdit}
                                                        className="datetime-picker"
                                                        format={`MM/DD/YYYY hh:mm:ss A ${timezoneCode}`}
                                                    />
                                                    {!enableEdit && (
                                                        <Button
                                                            size="small"
                                                            onClick={() =>
                                                                formik.setFieldValue(
                                                                    "newSeasonDate",
                                                                    null
                                                                )
                                                            }
                                                        >
                                                            Clear
                                                        </Button>
                                                    )}
                                                </Box>
                                            </LocalizationProvider>
                                        </Grid>
                                    </Grid>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </form>
            </Grid>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleConfirmDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {dialogContent.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {dialogContent.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleConfirmDialogClose}
                        color="primary"
                        sx={{
                            height: "44px",
                            padding: "0 40px 0 32px",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleConfirmApply(true)}
                        color="primary"
                        variant="outlined"
                        sx={{
                            height: "44px",
                            padding: "0 40px 0 32px",
                        }}
                    >
                        {dialogContent.applyAllButtonText}
                    </Button>
                    <Button
                        onClick={() => handleConfirmApply(false)}
                        color="primary"
                        variant="contained"
                    >
                        {dialogContent.applyButtonText}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MetadataForm;
